import _ from 'lodash';
import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import { AusDecisionType } from 'constants/loanApplication';
import {
  lazyLoadedPipelineColumns,
  LoanPipelineNodeType,
  LoanPipelineSpecificColumnId,
} from 'constants/loanPipelines';
import { getColumnDisplayMode, getLoanPipelineColumnDefinition } from 'utils/pipelines';

import { selectNormalizedPipelinesColumnDefinitions } from 'store/system/selectors';

const selectLoanPipelines = ({ loanPipelines }) => loanPipelines.loanPipelinesData;

// Raw store lookup — presence check only, without the column-definition mapping
// selectLoanPipelineById does. A pipeline absent here does not belong to this session's
// list (e.g. another company's pipeline referenced by a SignalR event).
export const selectRawLoanPipelineById = pipelineId => state =>
  selectLoanPipelines(state)[pipelineId] ?? null;

export const selectLoanPipelineById = memoizeOne(pipelineId =>
  createSelector(
    [selectLoanPipelines, selectNormalizedPipelinesColumnDefinitions],
    (loanPipelines, normalizedColumnDefinitions) => {
      const loanPipeline = loanPipelines[pipelineId];

      if (!loanPipeline) {
        return null;
      }

      return {
        ...loanPipeline,
        hasLazyViewColumns: loanPipeline.viewColumns?.some(({ id }) =>
          lazyLoadedPipelineColumns.includes(id),
        ),
        isSortDisabled: !!loanPipeline.outputRule?.sortingCriteria?.length,
        viewColumns: loanPipeline.viewColumns?.reduce((acc, viewColumn) => {
          const primaryDefinitionId = viewColumn.valueEntityId || viewColumn.id;
          const displayMode = getColumnDisplayMode(viewColumn.metadata);
          const definitionsByPrimaryId =
            normalizedColumnDefinitions[loanPipeline.representationView?.type];
          const definition = getLoanPipelineColumnDefinition({
            definitionsByPrimaryId,
            primaryDefinitionId,
            columnId: viewColumn.id,
          });

          if (viewColumn.id === LoanPipelineSpecificColumnId.AUS_DECISIONS) {
            acc.push(
              {
                ...definition,
                ...viewColumn,
                displayMode,
                parentId: viewColumn.id,
                id: AusDecisionType.DO,
              },
              {
                ...definition,
                ...viewColumn,
                displayMode,
                parentId: viewColumn.id,
                id: AusDecisionType.LPURLA,
              },
            );

            return acc;
          }

          acc.push({ ...definition, ...viewColumn, displayMode, primaryDefinitionId });

          return acc;
        }, []),
      };
    },
  ),
);

const selectLoanPipelinesList = createSelector(selectLoanPipelines, Object.values);

export const selectIsLoanPipelinesListEmpty = createSelector(
  selectLoanPipelinesList,
  pipelines => !pipelines.length,
);

const selectSortedLoanPipelinesList = createSelector(selectLoanPipelinesList, pipelines =>
  _.sortBy(pipelines, ['orderNumber']),
);

const toPipelineOption = ({
  name,
  icon,
  id,
  countOfItems,
  representationView,
  nodeType,
  parentId,
  widgetConfiguration,
}) => ({
  name,
  icon,
  value: id,
  countOfItems,
  representationView,
  nodeType: nodeType ?? LoanPipelineNodeType.PIPELINE,
  parentId: parentId ?? null,
  badgeConditionalColoring: widgetConfiguration?.badgeConditionalColoring ?? null,
  badgeThresholds: widgetConfiguration?.badgeThresholds ?? null,
});

const selectSortedLoanPipelinesOptions = createSelector(selectSortedLoanPipelinesList, pipelines =>
  pipelines.map(toPipelineOption),
);

const ROOT_PARENT_KEY = '__root__';

const rollUpCount = (option, childrenByParent) =>
  option.nodeType === LoanPipelineNodeType.GROUP
    ? _.sumBy(childrenByParent[option.value] ?? [], child => rollUpCount(child, childrenByParent))
    : option.countOfItems || 0;

const flattenTree = (parentKey, depth, childrenByParent) =>
  (childrenByParent[parentKey] ?? []).flatMap(option => {
    if (option.nodeType === LoanPipelineNodeType.GROUP) {
      const childNodes = flattenTree(option.value, depth + 1, childrenByParent);

      // Suppress a group whose visible subtree is empty (its children were security-filtered,
      // or it's a genuinely empty group) — a lone header would disclose the group name/existence.
      if (childNodes.length === 0) {
        return [];
      }

      return [
        { ...option, depth, isGroup: true, rolledUpCount: rollUpCount(option, childrenByParent) },
        ...childNodes,
      ];
    }

    return [{ ...option, depth, isGroup: false, rolledUpCount: option.countOfItems || 0 }];
  });

// Depth-first, sibling-ordered flat list for the navigator tree: each entry carries
// its `depth`, `isGroup`, and (for groups) the rolled-up descendant pipeline count.
export const selectLoanPipelinesTreeOptions = createSelector(
  selectSortedLoanPipelinesOptions,
  options => {
    const childrenByParent = _.groupBy(options, option => option.parentId ?? ROOT_PARENT_KEY);

    return flattenTree(ROOT_PARENT_KEY, 0, childrenByParent);
  },
);

export const selectLoanPipelineNameById = memoizeOne(pipelineId =>
  createSelector(selectLoanPipelines, loanPipelines => loanPipelines[pipelineId]?.name),
);
